import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Webhook, Send, RefreshCw, Copy } from "lucide-react";

const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copiato"));

// Configurazione webhook per una chiave API: URL https, secret HMAC, invio di prova
export function WebhookConfig({ k, onChanged }) {
  const [url, setUrl] = useState(k.webhook_url || "");
  const [collId, setCollId] = useState(k.collection_id || "");
  const [colls, setColls] = useState([]);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  useEffect(() => { api.get("/collections").then((r) => setColls(r.data || [])).catch(() => {}); }, []);
  const save = async (rotate = false) => {
    setBusy(true);
    try { await api.patch(`/ext-keys/${k.id}/webhook`, { webhook_url: url, rotate_secret: rotate, collection_id: collId || null }); toast.success(rotate ? "Secret rigenerato" : "Webhook salvato"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true); setTest(null);
    try { const { data } = await api.post(`/ext-keys/${k.id}/webhook/test`); setTest(data); (data.status === "ok" ? toast.success : toast.error)(data.status === "ok" ? `Consegnato (HTTP ${data.http_status})` : `Fallito: ${data.error}`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 rounded-lg bg-zinc-50 ring-1 ring-zinc-200 p-3 space-y-2 text-xs" data-testid={`webhook-config-${k.id}`}>
      <div className="font-semibold flex items-center gap-1.5"><Webhook className="h-3.5 w-3.5" /> Webhook "saldo cambiato" verso il portale</div>
      <div className="flex flex-wrap gap-2 items-center">
        <select value={collId} onChange={(e) => setCollId(e.target.value)} className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-xs" data-testid={`webhook-collection-${k.id}`}>
          <option value="">Tutte le raccolte (default: principale)</option>
          {colls.map((c) => <option key={c.id} value={c.id}>Raccolta collegata: {c.name}</option>)}
        </select>
        <span className="text-[11px] text-zinc-500">Con una raccolta collegata, gli accrediti via API finiscono su quella raccolta e il gestionale esterno riceve solo i suoi movimenti (campo <code>collection</code>).</span>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://portale.esempio.it/webhooks/nouvelle" className="h-8 text-xs flex-1 min-w-[260px]" data-testid={`webhook-url-${k.id}`} />
        <Button size="sm" variant="outline" disabled={busy} onClick={() => save(false)} data-testid={`webhook-save-${k.id}`}>Salva</Button>
        {k.webhook_url && <Button size="sm" variant="outline" disabled={busy} onClick={send} data-testid={`webhook-test-${k.id}`}><Send className="h-3.5 w-3.5 mr-1" /> Invia prova</Button>}
      </div>
      {k.webhook_secret && (
        <div className="flex flex-wrap items-center gap-2 text-zinc-600">
          Secret firma: <code className="font-mono bg-white px-1.5 py-0.5 rounded ring-1 ring-zinc-200" data-testid={`webhook-secret-${k.id}`}>{k.webhook_secret}</code>
          <button type="button" onClick={() => copy(k.webhook_secret)} className="p-1 rounded hover:bg-zinc-200" aria-label="Copia secret" data-testid={`webhook-copy-secret-${k.id}`}><Copy className="h-3.5 w-3.5" /></button>
          <button type="button" onClick={() => save(true)} disabled={busy} className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-900" data-testid={`webhook-rotate-${k.id}`}><RefreshCw className="h-3 w-3" /> Rigenera</button>
        </div>
      )}
      {test && <div className={test.status === "ok" ? "text-emerald-700" : "text-red-600"} data-testid={`webhook-test-result-${k.id}`}>Prova: {test.status === "ok" ? "consegnata" : "fallita"} · tentativi {test.attempts} · {test.http_status ? `HTTP ${test.http_status}` : test.error}</div>}
    </div>
  );
}

export function WebhookLog({ rows }) {
  if (!rows?.length) return <p className="text-sm text-zinc-500" data-testid="webhook-log-empty">Nessuna notifica inviata ai portali.</p>;
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
  return (
    <div className="overflow-x-auto"><table className="w-full text-xs">
      <thead><tr className="text-left text-zinc-500"><th className="py-1 pr-3">Quando</th><th className="pr-3">Portale</th><th className="pr-3">Carta</th><th className="pr-3 text-right">Δ punti</th><th className="pr-3">Tentativi</th><th>Esito</th></tr></thead>
      <tbody>{rows.map((d) => (
        <tr key={d.id} className="border-t border-zinc-200" data-testid={`webhook-delivery-${d.id}`}>
          <td className="py-1.5 pr-3 whitespace-nowrap">{fmt(d.created_at)}</td><td className="pr-3">{d.key_name}{d.test ? " (prova)" : ""}</td><td className="pr-3 font-mono">{d.card_number}</td>
          <td className="pr-3 text-right font-semibold">{d.points_delta > 0 ? `+${d.points_delta}` : d.points_delta}</td><td className="pr-3">{d.attempts}</td>
          <td>{d.status === "ok" ? <span className="text-emerald-700">OK · HTTP {d.http_status}</span> : <span className="text-red-600">Fallito · {d.error}</span>}</td>
        </tr>))}</tbody>
    </table></div>
  );
}
